import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, TrendingUp, TrendingDown, AlertTriangle, Users, DollarSign, BarChart3, Search } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService } from '@/services/businessService'

export function LenderDashboardPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState({
    totalBusinesses: 0,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
    avgScore: 0,
    recentAlerts: 0
  })
  const [recentBusinesses, setRecentBusinesses] = useState<any[]>([])
  const navigate = useNavigate()
  const showToast = useToast()

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setIsLoading(true)
    try {
      const response = await businessService.getBusinesses({ limit: 10 })
      
      // Calculate statistics
      const businesses = response.businesses
      const scores = businesses.map(b => parseFloat(b.score || '0'))
      const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length

      const riskCounts = {
        low: businesses.filter(b => parseFloat(b.score || '0') >= 75).length,
        medium: businesses.filter(b => {
          const score = parseFloat(b.score || '0')
          return score >= 55 && score < 75
        }).length,
        high: businesses.filter(b => parseFloat(b.score || '0') < 55).length
      }

      setStats({
        totalBusinesses: response.pagination.total,
        highRisk: riskCounts.high,
        mediumRisk: riskCounts.medium,
        lowRisk: riskCounts.low,
        avgScore: avgScore,
        recentAlerts: riskCounts.high + Math.floor(riskCounts.medium / 2)
      })

      setRecentBusinesses(businesses.slice(0, 5))
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load dashboard data.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getRiskColor = (score: string) => {
    const s = parseFloat(score || '0')
    if (s >= 75) return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
    if (s >= 55) return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
    return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading dashboard...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Lender Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Monitor and assess business risks across your portfolio
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Businesses</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stats.totalBusinesses}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">12%</span>
            <span className="text-gray-600 dark:text-gray-400 ml-2">vs last month</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Score</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stats.avgScore.toFixed(1)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">5.2%</span>
            <span className="text-gray-600 dark:text-gray-400 ml-2">improvement</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Risk</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stats.highRisk}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingDown className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">8%</span>
            <span className="text-gray-600 dark:text-gray-400 ml-2">decrease</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Alerts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stats.recentAlerts}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-yellow-600 font-medium">Requires attention</span>
          </div>
        </div>
      </div>

      {/* Risk Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Risk Distribution</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Low Risk</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.lowRisk} ({((stats.lowRisk / stats.totalBusinesses) * 100).toFixed(1)}%)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: `${(stats.lowRisk / stats.totalBusinesses) * 100}%` }}></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Medium Risk</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.mediumRisk} ({((stats.mediumRisk / stats.totalBusinesses) * 100).toFixed(1)}%)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-yellow-600 h-2 rounded-full" style={{ width: `${(stats.mediumRisk / stats.totalBusinesses) * 100}%` }}></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">High Risk</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.highRisk} ({((stats.highRisk / stats.totalBusinesses) * 100).toFixed(1)}%)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-red-600 h-2 rounded-full" style={{ width: `${(stats.highRisk / stats.totalBusinesses) * 100}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/lender/search')}
              className="flex flex-col items-center justify-center p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <Search className="w-8 h-8 text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Search Businesses</span>
            </button>

            <button
              onClick={() => navigate('/lender/comparison')}
              className="flex flex-col items-center justify-center p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <BarChart3 className="w-8 h-8 text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Compare Scores</span>
            </button>

            <button
              onClick={() => navigate('/lender/portfolio')}
              className="flex flex-col items-center justify-center p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">View Portfolio</span>
            </button>

            <button
              onClick={() => navigate('/lender/alerts')}
              className="flex flex-col items-center justify-center p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <AlertTriangle className="w-8 h-8 text-primary-600 dark:text-primary-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">View Alerts</span>
            </button>
          </div>
        </div>
      </div>

      {/* Recent Businesses */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recently Assessed Businesses</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {recentBusinesses.map((business) => (
            <div
              key={business.id}
              className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              onClick={() => navigate(`/lender/business/${business.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{business.business_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{business.industry_sector}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{parseFloat(business.score || '0').toFixed(2)}</p>
                    <p className={`text-xs font-medium px-2 py-0.5 rounded-full ${getRiskColor(business.score)}`}>
                      {business.band}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => navigate('/lender/search')}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-sm"
          >
            View all businesses →
          </button>
        </div>
      </div>
    </div>
  )
}